import { useQuery } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { authStore } from '@/services/auth/store';
import { AccountState, VerificationReviewState, VerificationState } from '@/services/auth/types';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isSendingVerified } from '@/services/db/domain/entity';
import { useDomainsOptional } from '@/services/db/domain/queries';
import { getEmailSetupStatusQueryOptions } from '@/services/db/email-setup-status/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';

export type AccountEmailSetup = {
  hasVerifiedSendingDomain: boolean;
  hasFromAddress: boolean;
};

const EMPTY_EMAIL_SETUP: AccountEmailSetup = { hasVerifiedSendingDomain: false, hasFromAddress: false };

/**
 * Account-wide email-setup status (verified sending domain + From address).
 *
 * Shares `getEmailSetupStatusQueryOptions` rather than re-fetching: both read the
 * same endpoint under the same cache key, so the cache has to hold one shape.
 * It holds the API's, and this camel-cases it through `select` for its callers.
 */
export function useAccountEmailSetup({ enabled = true }: { enabled?: boolean } = {}) {
  const auth = useStore(authStore);
  const accountId = auth.account?.id;

  return useQuery({
    ...getEmailSetupStatusQueryOptions(accountId ?? 0),
    select: (data): AccountEmailSetup => ({
      hasVerifiedSendingDomain: data.has_verified_sending_domain,
      hasFromAddress: data.has_from_address,
    }),
    enabled: accountId != null && enabled,
  });
}

type CanSelectNormalDeliveryInput = {
  action: 'create' | 'edit';
  testDeliveryEnabled: boolean;
  isBuilder: boolean;
  builderMustUpgrade: boolean;
  verificationApproved: boolean;
  /** Verified sending domain — the builder's `canEnableMessageSending` gate (Ember `isSendingDomainSetUp`). */
  hasVerifiedSendingDomain: boolean;
  /** Verified sending domain AND a from address — the create-waiver requirement. */
  accountHasEmailSetup: boolean;
};

/**
 * Whether the "Send messages to your audience" (normal) delivery option can be
 * selected. Port of the Ember form's `canSelectNormalDelivery` composed with
 * the test-delivery-mode service's `canEnableMessageSending`.
 *
 * @public exported for unit testing
 */
// fallow-ignore-next-line complexity -- faithful port of the Ember canSelectNormalDelivery gating rules; estimated-coverage CRAP, covered by unit tests
export function computeCanSelectNormalDelivery({
  action,
  testDeliveryEnabled,
  isBuilder,
  builderMustUpgrade,
  verificationApproved,
  hasVerifiedSendingDomain,
  accountHasEmailSetup,
}: CanSelectNormalDeliveryInput): boolean {
  // With test-delivery mode off, normal sending is always selectable.
  if (!testDeliveryEnabled) {
    return true;
  }

  // `canEnableMessageSending`: builders (on a paid plan) need a verified sending domain only —
  // matching Ember's `isSendingDomainSetUp` (a from address is NOT required here); everyone
  // else needs approved verification. (Account-wide status approximates Ember's env-scoped check.)
  const canEnableMessageSending = isBuilder ? !builderMustUpgrade && hasVerifiedSendingDomain : verificationApproved;
  if (canEnableMessageSending) {
    return true;
  }

  // Beyond that, only the create form waives the per-workspace requirement when
  // the account already has email set up; edit stops here.
  if (action !== 'create') {
    return false;
  }
  if (isBuilder && builderMustUpgrade) {
    return false;
  }
  if (!isBuilder && !verificationApproved) {
    return false;
  }
  return accountHasEmailSetup;
}

export type DeliveryModeGating = {
  canSelectNormalDelivery: boolean;
  /** The test-delivery-mode feature flag; when off, everything below is moot. */
  testDeliveryEnabled: boolean;
  /** Whether the account may leave test-delivery mode (Ember `canEnableMessageSending`). */
  canEnableMessageSending: boolean;
  /** The account's owner email — the default test-delivery address. */
  accountEmail: string;
  isBuilder: boolean;
  builderMustUpgrade: boolean;
  verificationApproved: boolean;
  verificationState: VerificationState | undefined;
  verificationNeedsMoreInfo: boolean;
  emailSetup: AccountEmailSetup;
  isLoading: boolean;
};

/**
 * Email-setup status for the delivery checklist + gating. On edit, Ember scoped the
 * domain/from-address check to the workspace being edited (`accountWideEmailSetupCheck` is false
 * for edit) rather than the account, so read this workspace's verified sending domains and email
 * identities (from addresses). On create there is no workspace yet, so both env queries stay inert
 * and callers fall back to the account-wide status. Returns both scopes plus the loading state.
 */
function useEmailSetupForAction(action: 'create' | 'edit') {
  const isEdit = action === 'edit';
  const accountQuery = useAccountEmailSetup();
  const accountEmailSetup = accountQuery.data ?? EMPTY_EMAIL_SETUP;

  const domainsQuery = useDomainsOptional({
    enabled: isEdit,
    // isSendingVerified, not verified alone: a disabled domain would unlock
    // normal delivery and then send nothing.
    select: (data) => data.domains.filter((domain) => isSendingVerified(domain)).length,
  });
  const environmentQuery = useEnvironmentOptional();
  const emailIdentityCount = (environmentQuery.data?.identities ?? []).filter(
    (identity) => identity?.template_type === 'email',
  ).length;
  const envEmailSetup: AccountEmailSetup = {
    hasVerifiedSendingDomain: (domainsQuery.data ?? 0) > 0,
    hasFromAddress: emailIdentityCount > 0,
  };

  return {
    // Checklist + builder "can enable sending" gate read env-scoped status on edit, account-wide
    // on create; the create-only normal-delivery waiver is always account-wide.
    emailSetup: isEdit ? envEmailSetup : accountEmailSetup,
    accountHasEmailSetup: accountEmailSetup.hasVerifiedSendingDomain && accountEmailSetup.hasFromAddress,
    isLoading: isEdit ? domainsQuery.isLoading || environmentQuery.isLoading : accountQuery.isLoading,
  };
}

/** Resolves the delivery-mode gating signals the form's delivery section needs. */
export function useDeliveryModeGating(action: 'create' | 'edit'): DeliveryModeGating {
  const auth = useStore(authStore);
  const account = auth.account;
  const testDeliveryEnabled = useFeatureFlag(FeatureFlag.TestDeliveryMode);
  const { emailSetup, accountHasEmailSetup, isLoading } = useEmailSetupForAction(action);

  const isBuilder = isBuilderPlan(account);
  const builderMustUpgrade = isBuilder && account?.account_state === AccountState.Free;
  const verificationApproved = account?.verification_state === VerificationState.Approved;
  const canEnableMessageSending =
    !testDeliveryEnabled ||
    (isBuilder ? !builderMustUpgrade && emailSetup.hasVerifiedSendingDomain : verificationApproved);

  return {
    canSelectNormalDelivery: computeCanSelectNormalDelivery({
      action,
      testDeliveryEnabled,
      isBuilder,
      builderMustUpgrade,
      verificationApproved,
      hasVerifiedSendingDomain: emailSetup.hasVerifiedSendingDomain,
      accountHasEmailSetup,
    }),
    testDeliveryEnabled,
    canEnableMessageSending,
    accountEmail: account?.email ?? '',
    isBuilder,
    builderMustUpgrade,
    verificationApproved,
    verificationState: account?.verification_state,
    verificationNeedsMoreInfo: account?.verification_review_state === VerificationReviewState.NeedsMoreInfo,
    emailSetup,
    isLoading,
  };
}
